// ============================================================================
// HoneyLocker — Customer 360 + cross-provider message search (frontend).
// Talks only to /api/lookup (all provider keys stay server-side). Every lookup
// is audit-logged by the backend; this UI just renders the consolidated result.
// ============================================================================
import { api } from '../lib/api.js'
import { esc } from '../lib/view.js'

// --- small formatters ------------------------------------------------------
function fmtWhen(at) {
  if (!at) return '—'
  const d = new Date(at)
  if (isNaN(d)) return esc(String(at))
  return esc(d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }))
}
function fmtPhone(p) {
  const d = String(p || '').replace(/\D/g, '').replace(/^1/, '')
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : esc(String(p || '—'))
}
function fmtMoney(n) {
  if (n == null || n === '') return null
  const v = Number(n)
  if (isNaN(v)) return esc(String(n))
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 })
}
const dirPill = (d) => d === 'in'
  ? '<span class="pill an">IN</span>'
  : '<span class="pill mk">OUT</span>'
const provPill = (p) => p === 'SignalHouse'
  ? '<span class="pill an">SignalHouse</span>'
  : `<span class="pill">${esc(p)}</span>`

// --- result renderers ------------------------------------------------------
function profileCard(p, funding) {
  if (!p) return ''
  const kv = []
  if (p.email) kv.push(['Email', esc(p.email)])
  if (p.phone) kv.push(['Phone', fmtPhone(p.phone)])
  const loc = [p.city, p.region, p.country].filter(Boolean).join(', ')
  if (loc) kv.push(['Location', esc(loc)])
  if (p.timezone) kv.push(['Timezone', esc(p.timezone)])
  if (p.partner) kv.push(['Partner', esc(p.partner)])
  if (p.orderId) kv.push(['Order ID', esc(p.orderId)])
  if (p.flashId) kv.push(['User ID', esc(p.flashId)])
  if (p.createdAt) kv.push(['CIO since', fmtWhen(p.createdAt)])

  const fund = funding ? `
    <div class="stat" style="margin-top:14px;max-width:320px">
      <div class="k">Funded${funding.count > 1 ? ` · ${funding.count} contracts` : ''}</div>
      <div class="v">${fmtMoney(funding.approvedAmount) || '—'}</div>
      <div class="d">
        ${funding.contractId ? `Contract ${esc(funding.contractId)}` : ''}${funding.signedDate ? ` · ${esc(funding.signedDate)}` : ''}${funding.channel ? ` · ${esc(funding.channel)}` : ''}
      </div>
    </div>` : ''

  const rows = kv.map(([k, v]) => `
    <div style="display:flex;gap:10px;padding:5px 0;font-size:13.5px">
      <div style="min-width:96px;color:var(--muted);font-weight:700">${k}</div>
      <div>${v}</div>
    </div>`).join('')

  return `
    <div class="card pad" style="margin-bottom:16px">
      <div style="display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;margin-bottom:8px">
        <h2 style="margin:0">${esc(p.name || 'Unknown name')}</h2>
        ${funding ? '<span class="pill an">Funded customer</span>' : ''}
        ${p.cioUrl ? `<a class="pill" href="${esc(p.cioUrl)}" target="_blank" rel="noopener" style="text-decoration:none">Customer.io ↗</a>` : ''}
        ${p.flashCustomerUrl ? `<a class="pill" href="${esc(p.flashCustomerUrl)}" target="_blank" rel="noopener" style="text-decoration:none">Flash customer ↗</a>` : ''}
        ${p.flashOrderUrl ? `<a class="pill" href="${esc(p.flashOrderUrl)}" target="_blank" rel="noopener" style="text-decoration:none">Flash order ↗</a>` : ''}
      </div>
      ${rows}
      ${fund}
    </div>`
}

function messagesCard(messages) {
  if (!messages || !messages.length) {
    return `<div class="card pad"><div class="mod-h">Messages</div><div class="empty">No cross-provider messages found for this number.</div></div>`
  }
  // /api/lookup returns all messages at once, so paging is client-side over the
  // already-fetched array; wireMessages() (called after innerHTML) handles it.
  return `
    <div class="card pad">
      <div class="toolbar">
        <div class="mod-h" style="margin:0">Messages <span class="pill" style="font-weight:700">${messages.length}</span></div>
        <span class="grow"></span>
        <select class="h-input" id="lk-msg-size" style="width:auto">
          <option value="25">25 / page</option>
          <option value="50">50 / page</option>
          <option value="100">100 / page</option>
        </select>
        <button class="btn ghost" id="lk-msg-export">Export CSV</button>
      </div>
      <div class="table-wrap">
        <table class="h-table">
          <thead><tr>
            <th>When</th><th>Dir</th><th>Provider</th><th>From → To</th><th>Status</th><th>Body</th>
          </tr></thead>
          <tbody id="lk-msg-rows"></tbody>
        </table>
      </div>
      <div class="toolbar" style="margin-top:12px;margin-bottom:0">
        <span class="mod-sub" id="lk-msg-count" style="margin:0"></span>
        <span class="grow"></span>
        <button class="btn ghost" id="lk-msg-prev">‹ Prev</button>
        <span class="mod-sub" id="lk-msg-page" style="margin:0"></span>
        <button class="btn ghost" id="lk-msg-next">Next ›</button>
      </div>
    </div>`
}

// Client-side pager + CSV export over the already-fetched messages array.
function wireMessages(box, messages) {
  if (!messages || !messages.length) return
  const $ = (id) => box.querySelector('#' + id)
  const rows = $('lk-msg-rows')
  if (!rows) return
  const total = messages.length
  let pageSize = 25, page = 0

  function render() {
    const start = page * pageSize
    const slice = messages.slice(start, start + pageSize)
    rows.innerHTML = slice.map((m) => `
      <tr>
        <td style="white-space:nowrap">${fmtWhen(m.at)}</td>
        <td>${dirPill(m.direction)}</td>
        <td>${provPill(m.provider)}</td>
        <td style="white-space:nowrap">${fmtPhone(m.from)} → ${fmtPhone(m.to)}</td>
        <td>${esc(m.status || '—')}</td>
        <td>${esc(m.body || '')}</td>
      </tr>`).join('')
    const from = total === 0 ? 0 : start + 1
    const to = start + slice.length
    const pages = Math.max(Math.ceil(total / pageSize), 1)
    $('lk-msg-count').textContent = `Showing ${from}–${to} of ${total}`
    $('lk-msg-page').textContent = `Page ${page + 1} of ${pages}`
    $('lk-msg-prev').disabled = page <= 0
    $('lk-msg-next').disabled = page >= pages - 1
    $('lk-msg-prev').style.opacity = page <= 0 ? '.5' : '1'
    $('lk-msg-next').style.opacity = page >= pages - 1 ? '.5' : '1'
  }

  // ---- CSV export (all messages) ----
  function toCsv(list) {
    const cols = ['when', 'dir', 'provider', 'from', 'to', 'status', 'body']
    const val = (m, col) =>
      col === 'when' ? (m.at ? new Date(m.at).toISOString() : '')
      : col === 'dir' ? (m.direction === 'in' ? 'IN' : 'OUT')
      : col === 'from' ? String(m.from ?? '')
      : col === 'to' ? String(m.to ?? '')
      : m[col] == null ? '' : String(m[col])
    const cell = (m, col) => {
      let v = val(m, col)
      if (/^[=+\-@\t\r]/.test(v)) v = "'" + v; return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v
    }
    return [cols.join(','), ...list.map(m => cols.map(col => cell(m, col)).join(','))].join('\n')
  }

  $('lk-msg-export').addEventListener('click', () => {
    try {
      const csv = toCsv(messages)
      const blob = new Blob([csv], { type: 'text/csv' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `hive-messages-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(a.href)
    } catch (e) { alert('Export failed: ' + e.message) }
  })

  $('lk-msg-size').addEventListener('change', () => { pageSize = parseInt($('lk-msg-size').value) || 25; page = 0; render() })
  $('lk-msg-prev').addEventListener('click', () => { if (page > 0) { page--; render() } })
  $('lk-msg-next').addEventListener('click', () => { page++; render() })

  render()
}

// --- Recent activity (Customer.io) -----------------------------------------
// CIO timestamps are unix SECONDS; fmtWhen wants ms.
function fmtActivityWhen(ts) {
  if (!ts) return '—'
  let ms = Number(ts)
  if (!Number.isFinite(ms)) return fmtWhen(ts)
  if (ms < 1e12) ms *= 1000
  return fmtWhen(ms)
}
// Channel of a metric activity → 'SMS' | 'Email' | 'Push' | 'Action' | ''.
function channelLabel(a) {
  const c = String(a.delivery_type || a.channel || (a.data && (a.data.delivery_type || a.data.channel)) || '').toLowerCase()
  if (c.includes('sms') || c.includes('twilio') || c.includes('signal')) return 'SMS'
  if (c.includes('email')) return 'Email'
  if (c.includes('push')) return 'Push'
  if (c.includes('webhook') || c.includes('slack')) return 'Action'
  const t = String(a.type || '').toLowerCase()
  if (t.endsWith('email')) return 'Email'
  if (t.endsWith('sms')) return 'SMS'
  return ''
}
// "Activity Type" column — matches Customer.io's display naming.
function activityType(a) {
  const t = String(a.type || '').toLowerCase()
  if (t === 'page') return 'Page View'
  if (t === 'attribute_change' || t === 'attributes_changed') return 'Attribute Change'
  if (t.includes('segment')) return 'Segment Membership Change'
  if (t === 'event') return 'Event'
  if (t === 'profile' || t === 'profile_created') return 'Profile Create'
  const metric = t.split('_')[0]
  const label = metric ? metric[0].toUpperCase() + metric.slice(1) : 'Activity'
  const ch = channelLabel(a)
  return ch ? `${label} ${ch}` : label
}
// "Activity Name" column — campaign/action name, page URL, subject, etc.
function activityName(a) {
  const t = String(a.type || '').toLowerCase()
  if (t === 'page') return a.name || (a.data && a.data.url) || ''
  return a.name || (a.data && (a.data.subject || a.data.recipient || a.data.url)) || '—'
}
function activityCard(activities) {
  if (!activities || !activities.length) return ''
  const rows = activities.map((a) => `
    <tr>
      <td style="white-space:nowrap">${esc(activityType(a))}</td>
      <td style="max-width:360px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(activityName(a))}</td>
      <td style="white-space:nowrap">${fmtActivityWhen(a.timestamp || a.created || a.time)}</td>
    </tr>`).join('')
  return `<div class="card pad" style="margin-top:16px">
    <div class="mod-h">Recent activity <span class="pill an" style="vertical-align:middle">Customer.io</span></div>
    <div class="table-wrap" style="margin-top:8px">
      <table class="h-table">
        <thead><tr><th>Activity Type</th><th>Activity Name</th><th>Timestamp</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  </div>`
}

function renderResult(box, data) {
  if (!data.profile && (!data.messages || !data.messages.length)) {
    box.innerHTML = `<div class="card pad"><div class="empty">No match for “${esc(data.q)}”.${data.note ? `<div style="margin-top:8px;font-size:12.5px">${esc(data.note)}</div>` : ''}</div></div>`
    return
  }
  const src = data.sources || {}
  const chip = (label, s) => {
    const cls = s && s.ok ? 'an' : ''
    const title = s && s.error ? ` title="${esc(s.error)}"` : ''
    return `<span class="pill ${cls}"${title}>${label}${s && s.ok ? '' : ' ·off'}</span>`
  }
  box.innerHTML = `
    ${profileCard(data.profile, data.funding)}
    ${activityCard(data.profile && data.profile.activities)}
    ${messagesCard(data.messages)}
    <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:12px;font-size:12px;color:var(--muted)">
      <span>Sources:</span>
      ${chip('Customer.io', src.cio)} ${chip('SignalHouse', src.signalhouse)} ${chip('Twilio', src.twilio)} ${chip('BigQuery', src.bq)}
      ${data.note ? `<span style="flex-basis:100%;margin-top:4px">${esc(data.note)}</span>` : ''}
    </div>`
  wireMessages(box, data.messages)
}

export function renderLocker(el) {
  el.innerHTML = `
    <div class="card pad" style="margin-bottom:16px">
      <div class="mod-h">HoneyLocker</div>
      <div class="mod-sub">Search by phone, email, name, or ID — every lookup is logged</div>
      <div class="toolbar" style="margin-bottom:0">
        <input class="h-input grow" id="lk-q" placeholder="Phone, email, name, or ID…" autocomplete="off" />
        <button class="btn" id="lk-go">Search</button>
      </div>
      <div class="msg bad" id="lk-msg"></div>
    </div>
    <div id="lk-results"></div>`

  const input = el.querySelector('#lk-q')
  const btn = el.querySelector('#lk-go')
  const msg = el.querySelector('#lk-msg')
  const results = el.querySelector('#lk-results')

  async function run() {
    const q = input.value.trim()
    msg.textContent = ''
    if (!q) { results.innerHTML = `<div class="card pad"><div class="empty">Enter a phone, email, name, or ID to begin.</div></div>`; return }
    btn.disabled = true
    const prev = btn.textContent
    btn.textContent = 'Searching…'
    results.innerHTML = `<div class="card pad"><div class="empty">Searching Customer.io, SignalHouse, Twilio &amp; BigQuery…</div></div>`
    try {
      const data = await api.get('/api/lookup?q=' + encodeURIComponent(q))
      renderResult(results, data)
    } catch (e) {
      results.innerHTML = ''
      msg.textContent = 'Lookup failed: ' + (e && e.message ? e.message : 'unknown error')
    } finally {
      btn.disabled = false
      btn.textContent = prev
    }
  }

  btn.addEventListener('click', run)
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); run() } })
  input.focus()
}
